import React, { useEffect, useRef } from "react";
import {
  gl_start, turnY, turnX, turnZ, mxm, perspective,
  setUniform, inverse, scale, move, drawMesh, identity
} from "./webgl5.js";

export default function WebGLCanvas() {

  function Scene() {

    // --- Cube mesh (12 triangles) ---
    const cubeMesh = {
      data: new Float32Array([
        -1,-1,-1, 0, 0,-1,  1,-1,-1, 0, 0,-1,  1, 1,-1, 0, 0,-1,
        1, 1,-1, 0, 0,-1, -1, 1,-1, 0, 0,-1, -1,-1,-1, 0, 0,-1,
        -1,-1, 1, 0, 0, 1,  1,-1, 1, 0, 0, 1,  1, 1, 1, 0, 0, 1,
        1, 1, 1, 0, 0, 1, -1, 1, 1, 0, 0, 1, -1,-1, 1, 0, 0, 1,

        -1,-1,-1, 0,-1, 0,  1,-1,-1, 0,-1, 0,  1,-1, 1, 0,-1, 0,
        1,-1, 1, 0,-1, 0, -1,-1, 1, 0,-1, 0, -1,-1,-1, 0,-1, 0,
        -1, 1,-1, 0, 1, 0,  1, 1,-1, 0, 1, 0,  1, 1, 1, 0, 1, 0,
        1, 1, 1, 0, 1, 0, -1, 1, 1, 0, 1, 0, -1, 1,-1, 0, 1, 0,

        -1,-1,-1,-1, 0, 0, -1, 1,-1,-1, 0, 0, -1, 1, 1,-1, 0, 0,
        -1, 1, 1,-1, 0, 0, -1,-1, 1,-1, 0, 0, -1,-1,-1,-1, 0, 0,
        1,-1,-1, 1, 0, 0,  1, 1,-1, 1, 0, 0,  1, 1, 1, 1, 0, 0, // right face
        1, 1, 1, 1, 0, 0,  1,-1, 1, 1, 0, 0,  1,-1,-1, 1, 0, 0, // right face
      ]),
      triangle_strip: false
    };

    // --- Octahedron mesh (8 triangles) ---
    const octaMesh = {
      data: new Float32Array([
        -1, 0, 0,-1,-1,-1,  0,-1, 0,-1,-1,-1,  0, 0,-1,-1,-1,-1,
        1, 0, 0, 1,-1,-1,  0,-1, 0, 1,-1,-1,  0, 0,-1, 1,-1,-1,

        -1, 0, 0,-1, 1,-1,  0, 1, 0,-1, 1,-1,  0, 0,-1,-1, 1,-1,
        1, 0, 0, 1, 1,-1,  0, 1, 0, 1, 1,-1,  0, 0,-1, 1, 1,-1,

        -1, 0, 0,-1,-1, 1,  0,-1, 0,-1,-1, 1,  0, 0, 1,-1,-1, 1,
        1, 0, 0, 1,-1, 1,  0,-1, 0, 1,-1, 1,  0, 0, 1, 1,-1, 1,
        
        -1, 0, 0,-1, 1, 1,  0, 1, 0,-1, 1, 1,  0, 0, 1,-1, 1, 1, // second face - flipped on x
        1, 0, 0, 1, 1, 1,  0, 1, 0, 1, 1, 1,  0, 0, 1, 1, 1, 1, // first face
      ]),
      triangle_strip: false
    };

    this.vertexShader = `#version 300 es
    uniform mat4 uMF, uMI;
    uniform float uTime;

    in vec3 aPos, aNor;
    out vec3 vPos, vNor;

    void main() {
      vec4 pos = uMF * vec4(aPos,1.0);
      vec4 nor = vec4(aNor,0.0) * uMI;
      gl_Position = pos;
      vPos = pos.xyz;
      vNor = nor.xyz;
    }`;

    this.fragmentShader = `#version 300 es
precision highp float;

in vec3 vPos, vNor;
uniform vec3 uColor;
uniform float uTime;
out vec4 fragColor;

// Phong lighting function for a point light
vec3 phongPointLight(vec3 N, vec3 pos, vec3 lightPos, vec3 viewDir, vec3 diffuseColor, vec4 specular) {
    vec3 L = normalize(lightPos - pos);               // vector from fragment to light
    vec3 R = 2.0 * N * dot(N,L) - L;                 // reflection
    float diff = max(dot(N,L), 0.0);

    float spec = pow(max(dot(R, viewDir), 0.0), specular.a);

    return diffuseColor * diff + specular.rgb * spec;
}

void main() {
    vec3 nor = normalize(vNor);
    vec3 viewDir = normalize(-vPos);                 // camera at origin
    vec3 lightPos = vec3(0.0, 0.0, 0.0);            // octahedron is the light source

    vec3 finalColor;

    if( uColor == vec3(1.0, 1.0, 1.0) ) {
        // Octahedron: self-emission + Phong
        vec3 diffuse = phongPointLight(nor, vPos, lightPos, viewDir, uColor, vec4(1.0, 1.0, 1.0, 16.0));
        vec3 emission = vec3(0.8 + sin(uTime * 5.0) * 1.0); // make the octahedron shine

        finalColor = diffuse + emission;
    } else {
        // Cubes: affected by octahedron light
        finalColor = phongPointLight(nor, vPos, lightPos, viewDir, uColor, vec4(1.0, 1.0, 1.0, 10.0));
    }

    fragColor = vec4(finalColor + 0.05, 1.0);
}

`;

    // --- Matrix class for push/pop ---
    function Matrix() {
      let m = [identity()], top = 0;
      this.push = () => { m[top+1] = m[top].slice(); top++; return this; }
      this.pop  = () => { if(top>0) top--; return this; }
      this.get  = () => m[top];
      this.identity = () => { m[top] = identity(); return this; }
      this.move = (x,y,z) => { m[top]=mxm(m[top], move(x,y,z)); return this; }
      this.turnX = a => { m[top] = mxm(m[top], turnX(a)); return this; }
      this.turnY = a => { m[top] = mxm(m[top], turnY(a)); return this; }
      this.turnZ = a => { m[top] = mxm(m[top], turnZ(a)); return this; }
      this.scale = (x,y,z) => { m[top]=mxm(m[top], scale(x,y,z)); return this; }
      this.perspective = (x,y,z) => { m[top]=mxm(m[top], perspective(x,y,z)); return this; }
    }

    let matrix = new Matrix();
    let startTime = Date.now()/1000;

    // --- draw helper ---
    const drawMyMesh = (mesh, color) => {
      setUniform('3fv','uColor', color);
      let m = matrix.get();
      setUniform('Matrix4fv','uMF',false,m);
      setUniform('Matrix4fv','uMI',false,inverse(m));
      drawMesh(mesh);
    }

    // --- Update function ---
    this.update = () => {
      let time = Date.now()/1000 - startTime;
      setUniform('1f','uTime',time);

      matrix.identity().perspective(0,0,-0.1);

      // 2 * 2 * 2 = 8 CUBES
      const r = 0.15;
      for(let i = 0; i < 8; i++){
        matrix.push();
          matrix.turnY(time * 0.6);
          matrix.turnX(time * 0.6);
          matrix.scale(r,r,r);

          const ix = (i&4)?1:-1;
          const iy = (i&2)?1:-1;
          const iz = (i&1)?1:-1;
          const moveAmt = 1.0 + Math.abs(Math.sin(time)) * 1.0;
          matrix.move(ix*moveAmt, iy*moveAmt, iz*moveAmt);

          drawMyMesh(cubeMesh, [0.48, 0.5, 0.68]); // bluish grey
        matrix.pop();
      }

      // --- Draw octahedron in center ---
      matrix.push();
        matrix.turnY(time);
        matrix.scale(0.15);
        drawMyMesh(octaMesh, [1, 1, 1]);
      matrix.pop();
    }
  }

  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if(!canvas) return;
    const scene = new Scene();
    gl_start(canvas, scene);
    return () => canvas.stopRender && canvas.stopRender();
  }, []);

  return (
    <div style={{ display:"flex", justifyContent:"center", alignItems:"center" }}>
      <canvas ref={canvasRef} width={400} height={400} style={{ background:"black", width:"400px", height:"400px" }} />
    </div>
  );
}